import { uniqueId } from 'lodash';
import { computed, IComputedValue, makeAutoObservable } from 'mobx';
import Gene, { StatusType, StatusTypes } from './Gene';
import GeneFactory from './GeneFactory';
import { DragAndDropType, GeneType, GeneTypes, OperonInfo } from './types';

export interface IOperon {
  copy: () => IOperon;
  id: string;
  info: OperonInfo;
  genes: Gene[];
  getGeneStatus: (
    geneType: GeneType,
    hasTransDominant?: boolean
  ) => StatusType | null;
  getIsFunctionalGene: (geneType: GeneType) => boolean;
  expressibleGenes: IComputedValue<Gene[]>;
  expressedOutput: IComputedValue<string>;
  hasDefaultGenes: IComputedValue<boolean>;
  isConstitutive: IComputedValue<boolean>;
  isConstitutiveDominant: IComputedValue<boolean>;
  isConstitutiveRecessive: IComputedValue<boolean>;
  isSuperRepressed: IComputedValue<boolean>;
  isTranscriptionBlocked: IComputedValue<boolean>;
  reset: () => void;
}

interface ILacOperon extends IOperon {
  lacI: Gene;
  lacP: Gene;
  lacO: Gene;
  lacZ: Gene;
  lacY: Gene;
  lacA: Gene;
}

export default class LacOperon implements ILacOperon {
  id: string;
  info: OperonInfo;
  lacI: Gene;
  lacP: Gene;
  lacO: Gene;
  lacZ: Gene;
  lacY: Gene;
  lacA: Gene;
  genes: Gene[];

  constructor(label: string, type: DragAndDropType) {
    makeAutoObservable(this);

    this.id = uniqueId();
    this.info = { label, prefix: 'Lac', type };
    this.lacI = GeneFactory.make(GeneTypes.LacI);
    this.lacP = GeneFactory.make(GeneTypes.LacP);
    this.lacO = GeneFactory.make(GeneTypes.LacO);
    this.lacZ = GeneFactory.make(GeneTypes.LacZ);
    this.lacY = GeneFactory.make(GeneTypes.LacY);
    this.lacA = GeneFactory.make(GeneTypes.LacA);
    this.genes = [
      this.lacI,
      this.lacP,
      this.lacO,
      this.lacZ,
      this.lacY,
      this.lacA,
    ];
  }

  copy = (): LacOperon => {
    const { label, type } = this.info;
    const copy = new LacOperon(label, type);

    copy.lacI.update(this.lacI.state);
    copy.lacP.update(this.lacP.state);
    copy.lacO.update(this.lacO.state);
    copy.lacZ.update(this.lacZ.state);
    copy.lacY.update(this.lacY.state);
    copy.lacA.update(this.lacA.state);

    return copy;
  };

  getGeneStatus(
    geneType: GeneType,
    hasTransDominant: boolean = false
  ): StatusType | null {
    const gene = this.expressibleGenes
      .get()
      .find((g) => g.info.type === geneType);

    if (!gene) {
      return null;
    }

    const defaultStatus = gene.state.status;

    if (!this.getIsFunctionalGene(geneType)) {
      return defaultStatus;
    }

    const isSuperRepressed =
      this.isSuperRepressed.get() && !this.isConstitutive.get();
    if (isSuperRepressed || defaultStatus === StatusTypes.NonInducible) {
      return StatusTypes.NonInducible;
    } else if (this.isConstitutive.get() && !hasTransDominant) {
      return StatusTypes.Constitutive;
    }

    return defaultStatus;
  }

  getIsFunctionalGene(geneType: GeneType): boolean {
    return [GeneTypes.LacZ, GeneTypes.LacY, GeneTypes.LacA].includes(geneType);
  }

  expressedOutput = computed(() => {
    let output = '';

    if (this.lacZ && this.lacZ.state.status !== StatusTypes.NonInducible) {
      output += 'Z';
    }
    if (this.lacY && this.lacY.state.status !== StatusTypes.NonInducible) {
      output += 'Y';
    }
    if (this.lacA && this.lacA.state.status !== StatusTypes.NonInducible) {
      output += 'A';
    }

    const isEmpty = output === '';
    if (isEmpty || this.isTranscriptionBlocked.get()) {
      return '';
    }

    return output;
  });

  expressibleGenes = computed(() => {
    return [this.lacI, this.lacO, this.lacZ, this.lacY, this.lacA];
  });

  hasDefaultGenes = computed(() =>
    this.expressibleGenes
      .get()
      .every((gene: Gene) => gene.state.status === StatusTypes.Inducible)
  );

  isConstitutive = computed(
    () =>
      this.isConstitutiveDominant.get() || this.isConstitutiveRecessive.get()
  );

  isConstitutiveDominant = computed(
    () => this.lacO.state.status === StatusTypes.Constitutive
  );

  isConstitutiveRecessive = computed(
    () => this.lacI.state.status === StatusTypes.Constitutive
  );

  isSuperRepressed = computed(
    () => this.lacI.state.status === StatusTypes.NonInducible
  );

  isTranscriptionBlocked = computed(
    () => this.isSuperRepressed.get() && !this.isConstitutive.get()
  );

  reset = () => {
    this.expressibleGenes.get().forEach((gene) => gene.reset());
  };
}
